import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Save, 
  Search, 
  Globe, 
  FileText, 
  Tag, 
  Image as ImageIcon, 
  AlertCircle,
  Settings,
  Edit3,
  Check,
  X,
  Info,
  Plus,
  Trash2,
  Eye,
  Copy,
  RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { blogService, collegeService } from '../lib/database';
import type { BlogPost, College } from '../lib/supabase';
import toast from 'react-hot-toast';
import SEOHead from '../components/SEOHead';

interface SeoSettings {
  [key: string]: {
    title: string;
    description: string;
    keywords: string;
    image?: string;
    canonical_url?: string;
    og_title?: string;
    og_description?: string;
    og_image?: string;
    twitter_title?: string;
    twitter_description?: string;
    focus_keyword?: string;
    schema_type?: string;
  };
}

interface ContentItem {
  id: string;
  title: string;
  slug: string;
  type: 'page' | 'blog' | 'college';
  category?: string;
  published?: boolean;
  url: string;
  seo: {
    title?: string;
    description?: string;
    keywords?: string;
    image?: string;
    canonical_url?: string;
    og_title?: string;
    og_description?: string;
    og_image?: string;
    twitter_title?: string;
    twitter_description?: string;
    focus_keyword?: string;
    schema_type?: string;
  };
}

const SEOManager = () => {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('pages');
  const [activePage, setActivePage] = useState('home');
  const [seoSettings, setSeoSettings] = useState<any>({});

  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [colleges, setColleges] = useState<College[]>([]);

  const [editingBlog, setEditingBlog] = useState<BlogPost | null>(null);
  const [editingCollege, setEditingCollege] = useState<College | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Content items
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [editingItem, setEditingItem] = useState<ContentItem | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  
  // Page SEO settings
  const [pageSeoSettings, setPageSeoSettings] = useState<SeoSettings>({
    home: {
      title: 'Grad Vikalp - Find Your Dream College | College Guidance Platform',
      description: 'Find your dream college with Grad Vikalp! Get verified student reviews, personalized college recommendations, and connect with real students. Your trusted partner for JEE, NEET, and college admissions guidance.',
      keywords: 'college admission, JEE colleges, NEET colleges, college reviews, personalized college list, engineering colleges, medical colleges, college guidance, student reviews, college comparison, admission counseling',
      image: '/Grad-Vikalp.png',
      canonical_url: '/',
      og_title: 'Grad Vikalp - Find Your Dream College',
      og_description: 'Find your dream college with Grad Vikalp! Verified reviews, AI-based college lists & real student connects – All in one place!',
      og_image: '/Grad-Vikalp.png',
      twitter_title: 'Grad Vikalp - Find Your Dream College',
      twitter_description: 'Find your dream college with Grad Vikalp! Verified reviews, AI-based college lists & real student connects – All in one place!',
      focus_keyword: 'college guidance',
      schema_type: 'WebSite'
    },
    about: {
      title: 'About Grad Vikalp - Your Trusted College Guidance Partner',
      description: 'Learn about Grad Vikalp\'s mission to democratize college selection through verified information, personalized guidance, and real student connections.',
      keywords: 'about grad vikalp, college guidance platform, education counseling, student success, college selection help',
      image: '/Grad-Vikalp.png',
      canonical_url: '/about',
      og_title: 'About Grad Vikalp - Your Trusted College Guidance Partner',
      og_description: 'Learn about Grad Vikalp\'s mission to democratize college selection through verified information, personalized guidance, and real student connections.',
      og_image: '/Grad-Vikalp.png',
      twitter_title: 'About Grad Vikalp',
      twitter_description: 'Your trusted college guidance partner',
      focus_keyword: 'college guidance platform',
      schema_type: 'AboutPage'
    },
    blog: {
      title: 'College Guidance Blog - Expert Tips & Admission Insights | Grad Vikalp',
      description: 'Get expert college guidance, admission tips, JEE & NEET preparation strategies, and career advice from our education experts.',
      keywords: 'college guidance blog, admission tips, JEE preparation, NEET guidance, college admission advice, career counseling',
      image: '/Grad-Vikalp.png',
      canonical_url: '/blog',
      og_title: 'College Guidance Blog - Expert Tips & Admission Insights',
      og_description: 'Get expert college guidance, admission tips, JEE & NEET preparation strategies, and career advice from our education experts.',
      og_image: '/Grad-Vikalp.png',
      twitter_title: 'College Guidance Blog',
      twitter_description: 'Expert tips & admission insights',
      focus_keyword: 'college guidance blog',
      schema_type: 'Blog'
    },
    'college-reviews': {
      title: 'College Reviews - Verified Student Reviews | Grad Vikalp',
      description: 'Read authentic college reviews from real students. Get insights about engineering, medical, and other colleges across India.',
      keywords: 'college reviews, student reviews, college ratings, engineering college reviews, medical college reviews',
      image: '/Grad-Vikalp.png',
      canonical_url: '/college-reviews',
      og_title: 'College Reviews - Verified Student Reviews',
      og_description: 'Read authentic college reviews from real students. Get insights about engineering, medical, and other colleges across India.',
      og_image: '/Grad-Vikalp.png',
      twitter_title: 'College Reviews',
      twitter_description: 'Verified student reviews',
      focus_keyword: 'college reviews',
      schema_type: 'CollectionPage'
    },
    'neet-college-predictor': {
      title: 'NEET College Predictor - AI-Powered Medical College Recommendations | Grad Vikalp',
      description: 'Get personalized medical college predictions based on your NEET rank, category, and preferences. AI-powered recommendations with admission probability analysis.',
      keywords: 'NEET college predictor, NEET college recommendations, medical college list, NEET admission probability',
      image: '/Grad-Vikalp.png',
      canonical_url: '/neet-college-predictor',
      og_title: 'NEET College Predictor - AI-Powered Recommendations',
      og_description: 'Get personalized medical college predictions based on your NEET rank, category, and preferences.',
      og_image: '/Grad-Vikalp.png',
      twitter_title: 'NEET College Predictor',
      twitter_description: 'AI-powered medical college recommendations',
      focus_keyword: 'NEET college predictor',
      schema_type: 'WebApplication'
    },
    'josaa-personalized-list': {
      title: 'JoSAA College Predictor - IIT, NIT, IIIT Admission Predictions | Grad Vikalp',
      description: 'Get accurate JoSAA college predictions for IIT, NIT, IIIT admissions based on your JEE Main and JEE Advanced ranks.',
      keywords: 'JoSAA college predictor, IIT admission predictor, NIT college predictor, JEE college recommendations',
      image: '/Grad-Vikalp.png',
      canonical_url: '/josaa-personalized-list',
      og_title: 'JoSAA College Predictor - IIT, NIT, IIIT Predictions',
      og_description: 'Get accurate JoSAA college predictions for IIT, NIT, IIIT admissions based on your JEE Main and JEE Advanced ranks.',
      og_image: '/Grad-Vikalp.png',
      twitter_title: 'JoSAA College Predictor',
      twitter_description: 'IIT, NIT, IIIT admission predictions',
      focus_keyword: 'JoSAA college predictor',
      schema_type: 'WebApplication'
    },
    contact: {
      title: 'Contact Grad Vikalp - Get College Guidance & Support',
      description: 'Contact Grad Vikalp for college guidance, admission counseling, and support. Reach out via email, phone, or our contact form.',
      keywords: 'contact grad vikalp, college guidance support, admission counseling, education help',
      image: '/Grad-Vikalp.png',
      canonical_url: '/contact',
      og_title: 'Contact Grad Vikalp - Get College Guidance & Support',
      og_description: 'Contact Grad Vikalp for college guidance, admission counseling, and support.',
      og_image: '/Grad-Vikalp.png',
      twitter_title: 'Contact Grad Vikalp',
      twitter_description: 'Get college guidance & support',
      focus_keyword: 'contact grad vikalp',
      schema_type: 'ContactPage'
    }
  });

  const pagesList = [
    { key: 'home', name: 'Home Page', url: '/' },
    { key: 'about', name: 'About Page', url: '/about' },
    { key: 'blog', name: 'Blog Listing', url: '/blog' },
    { key: 'college-reviews', name: 'College Reviews', url: '/college-reviews' },
    { key: 'neet-college-predictor', name: 'NEET Predictor', url: '/neet-college-predictor' },
    { key: 'josaa-personalized-list', name: 'JoSAA Predictor', url: '/josaa-personalized-list' },
    { key: 'contact', name: 'Contact Page', url: '/contact' }
  ];

  useEffect(() => {
    if (isAdmin) {
      fetchData();
      loadPageSEOSettings();
      loadSeoSettings();
      fetchContentItems();
    }
  }, [isAdmin]);

  const loadPageSEOSettings = async () => {
    try {
      const pages = ['home', 'about', 'blog', 'college-reviews', 'neet-college-predictor', 'josaa-personalized-list', 'contact'];
      const settings: any = {};
      
      for (const page of pages) {
        try {
          const pageSettings = await blogService.getPageSEO(page);
          if (pageSettings) {
            settings[page] = {
              meta_title: pageSettings.meta_title || '',
              meta_description: pageSettings.meta_description || '',
              meta_keywords: pageSettings.meta_keywords || '',
              focus_keyword: pageSettings.focus_keyword || '',
              canonical_url: pageSettings.canonical_url || '',
              og_title: pageSettings.og_title || '',
              og_description: pageSettings.og_description || '',
              og_image: pageSettings.og_image || '',
              twitter_title: pageSettings.twitter_title || '',
              twitter_description: pageSettings.twitter_description || '',
              schema_type: pageSettings.schema_type || 'WebPage'
            };
          } else {
            // Set default values if no settings exist
            settings[page] = getDefaultSEOForPage(page);
          }
        } catch (error) {
          console.error(`Error loading SEO for ${page}:`, error);
          settings[page] = getDefaultSEOForPage(page);
        }
      }
      
      setSeoSettings(settings);
    } catch (error) {
      console.error('Error loading page SEO settings:', error);
      toast.error('Failed to load SEO settings');
    }
  };

  const getDefaultSEOForPage = (page: string) => {
    const defaults: any = {
      home: {
        meta_title: 'Grad Vikalp - Find Your Dream College | College Guidance Platform',
        meta_description: 'Find your dream college with Grad Vikalp! Get verified student reviews, personalized college recommendations, and connect with real students. Your trusted partner for JEE, NEET, and college admissions guidance.',
        meta_keywords: 'college admission, JEE colleges, NEET colleges, college reviews, personalized college list, engineering colleges, medical colleges, college guidance, student reviews',
        focus_keyword: 'college guidance',
        canonical_url: '/',
        og_title: 'Grad Vikalp - Find Your Dream College',
        og_description: 'Find your dream college with Grad Vikalp! Verified reviews, AI-based college lists & real student connects – All in one place!',
        og_image: '/Grad-Vikalp.png',
        twitter_title: 'Grad Vikalp - Find Your Dream College',
        twitter_description: 'Find your dream college with Grad Vikalp! Verified reviews, AI-based college lists & real student connects – All in one place!',
        schema_type: 'WebPage'
      },
      about: {
        meta_title: 'About Grad Vikalp - Your Trusted College Guidance Partner',
        meta_description: 'Learn about Grad Vikalp\'s mission to democratize college selection through verified information, personalized guidance, and real student connections.',
        meta_keywords: 'about grad vikalp, college guidance platform, education counseling, student success, college selection help',
        focus_keyword: 'college guidance platform',
        canonical_url: '/about',
        og_title: 'About Grad Vikalp - Your Trusted College Guidance Partner',
        og_description: 'Learn about Grad Vikalp\'s mission to democratize college selection through verified information, personalized guidance, and real student connections.',
        og_image: '/Grad-Vikalp.png',
        twitter_title: 'About Grad Vikalp',
        twitter_description: 'Learn about Grad Vikalp\'s mission to democratize college selection.',
        schema_type: 'AboutPage'
      },
      blog: {
        meta_title: 'College Guidance Blog - Expert Tips & Admission Insights | Grad Vikalp',
        meta_description: 'Get expert college guidance, admission tips, JEE & NEET preparation strategies, and career advice from our education experts.',
        meta_keywords: 'college guidance blog, admission tips, JEE preparation, NEET guidance, college admission advice, career counseling',
        focus_keyword: 'college guidance blog',
        canonical_url: '/blog',
        og_title: 'College Guidance Blog - Expert Tips & Admission Insights',
        og_description: 'Get expert college guidance, admission tips, JEE & NEET preparation strategies, and career advice from our education experts.',
        og_image: '/Grad-Vikalp.png',
        twitter_title: 'College Guidance Blog',
        twitter_description: 'Get expert college guidance, admission tips, JEE & NEET preparation strategies.',
        schema_type: 'Blog'
      },
      'college-reviews': {
        meta_title: 'College Reviews - Verified Student Reviews | Grad Vikalp',
        meta_description: 'Read authentic college reviews from real students. Get insights about engineering, medical, and other colleges across India.',
        meta_keywords: 'college reviews, student reviews, college ratings, engineering college reviews, medical college reviews',
        focus_keyword: 'college reviews',
        canonical_url: '/college-reviews',
        og_title: 'College Reviews - Verified Student Reviews',
        og_description: 'Read authentic college reviews from real students. Get insights about engineering, medical, and other colleges across India.',
        og_image: '/Grad-Vikalp.png',
        twitter_title: 'College Reviews',
        twitter_description: 'Read authentic college reviews from real students.',
        schema_type: 'CollectionPage'
      },
      'neet-college-predictor': {
        meta_title: 'NEET College Predictor - AI-Powered Medical College Recommendations | Grad Vikalp',
        meta_description: 'Get personalized medical college predictions based on your NEET rank, category, and preferences. AI-powered recommendations with admission probability analysis.',
        meta_keywords: 'NEET college predictor, NEET college recommendations, medical college list, NEET admission probability',
        focus_keyword: 'NEET college predictor',
        canonical_url: '/neet-college-predictor',
        og_title: 'NEET College Predictor - AI-Powered Medical College Recommendations',
        og_description: 'Get personalized medical college predictions based on your NEET rank, category, and preferences.',
        og_image: '/Grad-Vikalp.png',
        twitter_title: 'NEET College Predictor',
        twitter_description: 'Get personalized medical college predictions based on your NEET rank.',
        schema_type: 'WebApplication'
      },
      'josaa-personalized-list': {
        meta_title: 'JoSAA College Predictor - IIT, NIT, IIIT Admission Predictions | Grad Vikalp',
        meta_description: 'Get accurate JoSAA college predictions for IIT, NIT, IIIT admissions based on your JEE Main and JEE Advanced ranks.',
        meta_keywords: 'JoSAA college predictor, IIT admission predictor, NIT college predictor, JEE college recommendations',
        focus_keyword: 'JoSAA college predictor',
        canonical_url: '/josaa-personalized-list',
        og_title: 'JoSAA College Predictor - IIT, NIT, IIIT Admission Predictions',
        og_description: 'Get accurate JoSAA college predictions for IIT, NIT, IIIT admissions based on your JEE Main and JEE Advanced ranks.',
        og_image: '/Grad-Vikalp.png',
        twitter_title: 'JoSAA College Predictor',
        twitter_description: 'Get accurate JoSAA college predictions for IIT, NIT, IIIT admissions.',
        schema_type: 'WebApplication'
      },
      contact: {
        meta_title: 'Contact Grad Vikalp - Get College Guidance & Support',
        meta_description: 'Contact Grad Vikalp for college guidance, admission counseling, and support. Reach out via email, phone, or our contact form.',
        meta_keywords: 'contact grad vikalp, college guidance support, admission counseling, education help',
        focus_keyword: 'college guidance support',
        canonical_url: '/contact',
        og_title: 'Contact Grad Vikalp - Get College Guidance & Support',
        og_description: 'Contact Grad Vikalp for college guidance, admission counseling, and support.',
        og_image: '/Grad-Vikalp.png',
        twitter_title: 'Contact Grad Vikalp',
        twitter_description: 'Contact Grad Vikalp for college guidance, admission counseling, and support.',
        schema_type: 'ContactPage'
      }
    };
    
    return defaults[page] || defaults.home;
  };

  const loadSeoSettings = () => {
    try {
      const savedSettings = localStorage.getItem('gradVikalpSeoSettings');
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        setPageSeoSettings(prev => ({ ...prev, ...parsedSettings }));
      }
    } catch (error) {
      console.error('Error loading SEO settings:', error);
    }
  };

  const fetchContentItems = async () => {
    try {
      setLoading(true);
      
      // Fetch blog posts
      const blogPosts = await blogService.getAll({});
      const blogItems: ContentItem[] = blogPosts.map(post => ({
        id: post.id,
        title: post.title,
        slug: post.slug,
        type: 'blog',
        category: post.category,
        published: post.published,
        url: `/blog/${post.slug}`,
        seo: {
          title: post.meta_title || post.title,
          description: post.meta_description || post.excerpt,
          keywords: post.meta_keywords || (post.tags ? post.tags.join(', ') : ''),
          image: post.og_image || post.image_url,
          canonical_url: post.canonical_url || `/blog/${post.slug}`,
          og_title: post.og_title || post.title,
          og_description: post.og_description || post.excerpt,
          og_image: post.og_image || post.image_url,
          twitter_title: post.twitter_title || post.title,
          twitter_description: post.twitter_description || post.excerpt,
          focus_keyword: post.focus_keyword,
          schema_type: post.schema_type || 'BlogPosting'
        }
      }));

      // Fetch colleges
      const colleges = await collegeService.getAll({});
      const collegeItems: ContentItem[] = colleges.map(college => ({
        id: college.id,
        title: college.name,
        slug: college.slug,
        type: 'college',
        category: college.category,
        published: true,
        url: `/college/${college.slug}`,
        seo: {
          title: college.meta_title || `${college.name} - College Details & Reviews | Grad Vikalp`,
          description: college.meta_description || college.description,
          keywords: college.meta_keywords || `${college.name}, ${college.category} college, ${college.location}, ${college.state}`,
          image: college.og_image || college.image_url,
          canonical_url: college.canonical_url || `/college/${college.slug}`,
          og_title: college.og_title || college.name,
          og_description: college.og_description || college.description,
          og_image: college.og_image || college.image_url,
          twitter_title: college.twitter_title || college.name,
          twitter_description: college.twitter_description || college.description,
          focus_keyword: college.focus_keyword,
          schema_type: college.schema_type || 'EducationalOrganization'
        }
      }));

      // Add page items
      const pageItems: ContentItem[] = pagesList.map(page => ({
        id: page.key,
        title: page.name,
        slug: page.key,
        type: 'page',
        published: true,
        url: page.url,
        seo: pageSeoSettings[page.key] || {}
      }));

      setContentItems([...pageItems, ...blogItems, ...collegeItems]);
    } catch (error) {
      console.error('Error fetching content items:', error);
      toast.error('Failed to load content items');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [blogData, collegeData] = await Promise.all([
        blogService.getAll({}),
        collegeService.getAll({})
      ]);
      
      setBlogs(blogData);
      setColleges(collegeData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handlePageSeoChange = (pageKey: string, field: string, value: string) => {
    setPageSeoSettings(prev => ({
      ...prev,
      [pageKey]: {
        ...prev[pageKey],
        [field]: value
      }
    }));
  };

  const handleContentSeoChange = (field: string, value: string) => {
    if (!editingItem) return;
    
    setEditingItem(prev => ({
      ...prev!,
      seo: {
        ...prev!.seo,
        [field]: value
      }
    }));
  };

  const savePageSEO = async () => {
    setSaving(true);
    try {
      const pageSettings = seoSettings[activePage];
      if (!pageSettings) {
        toast.error('No settings to save');
        return;
      }

      // Save to database
      await blogService.updatePageSEO(activePage, pageSettings);
      toast.success('✅ SEO settings saved and are now live on your website!');
    } catch (error) {
      console.error('Error saving page SEO:', error);
      toast.error('❌ Failed to save SEO settings to database');
    } finally {
      setSaving(false);
    }
  };

  const savePageSeoSettings = async () => {
    setSaving(true);
    try {
      // Save each page setting to database
      for (const [pageKey, settings] of Object.entries(pageSeoSettings)) {
        await blogService.updatePageSEO(pageKey, {
          meta_title: settings.title,
          meta_description: settings.description,
          meta_keywords: settings.keywords,
          og_image: settings.image,
          canonical_url: settings.canonical_url,
          og_title: settings.og_title,
          og_description: settings.og_description,
          twitter_title: settings.twitter_title,
          twitter_description: settings.twitter_description,
          focus_keyword: settings.focus_keyword,
          schema_type: settings.schema_type
        });
      }
      
      // Also save to localStorage as backup
      localStorage.setItem('gradVikalpSeoSettings', JSON.stringify(pageSeoSettings));
      toast.success('Page SEO settings saved successfully!');
    } catch (error) {
      console.error('Error saving page SEO settings:', error);
      toast.error('Failed to save page SEO settings');
    } finally {
      setSaving(false);
    }
  };

  const saveBlogSEO = async () => {
    if (!editingBlog) return;
    
    setSaving(true);
    try {
      await blogService.update(editingBlog.id, editingBlog);
      toast.success('✅ Blog SEO updated and is now live!');
      setEditingBlog(null);
      fetchData();
    } catch (error) {
      console.error('Error updating blog SEO:', error);
      toast.error('❌ Failed to update blog SEO');
    } finally {
      setSaving(false);
    }
  };

  const saveCollegeSEO = async () => {
    if (!editingCollege) return;
    
    setSaving(true);
    try {
      await collegeService.update(editingCollege.id, editingCollege);
      toast.success('✅ College SEO updated and is now live!');
      setEditingCollege(null);
      fetchData();
    } catch (error) {
      console.error('Error updating college SEO:', error);
      toast.error('❌ Failed to update college SEO');
    } finally {
      setSaving(false);
    }
  };

  const saveContentSeo = async () => {
    if (!editingItem) return;
    
    setSaving(true);
    try {
      if (editingItem.type === 'blog') {
        await blogService.update(editingItem.id, {
          meta_title: editingItem.seo.title,
          meta_description: editingItem.seo.description,
          meta_keywords: editingItem.seo.keywords,
          canonical_url: editingItem.seo.canonical_url,
          og_title: editingItem.seo.og_title,
          og_description: editingItem.seo.og_description,
          og_image: editingItem.seo.og_image,
          twitter_title: editingItem.seo.twitter_title,
          twitter_description: editingItem.seo.twitter_description,
          focus_keyword: editingItem.seo.focus_keyword,
          schema_type: editingItem.seo.schema_type
        });
      } else if (editingItem.type === 'college') {
        await collegeService.update(editingItem.id, {
          meta_title: editingItem.seo.title,
          meta_description: editingItem.seo.description,
          meta_keywords: editingItem.seo.keywords,
          canonical_url: editingItem.seo.canonical_url,
          og_title: editingItem.seo.og_title,
          og_description: editingItem.seo.og_description,
          og_image: editingItem.seo.og_image,
          twitter_title: editingItem.seo.twitter_title,
          twitter_description: editingItem.seo.twitter_description,
          focus_keyword: editingItem.seo.focus_keyword,
          schema_type: editingItem.seo.schema_type
        });
      }
      
      toast.success('SEO settings updated successfully!');
      setShowEditForm(false);
      setEditingItem(null);
      fetchContentItems();
    } catch (error) {
      console.error('Error saving content SEO:', error);
      toast.error('Failed to save SEO settings');
    } finally {
      setSaving(false);
    }
  };

  const editContentSeo = (item: ContentItem) => {
    setEditingItem(item);
    setShowEditForm(true);
  };

  const generateSeoFromContent = (item: ContentItem) => {
    if (!editingItem) return;
    
    let autoTitle = item.title;
    let autoDescription = '';
    let autoKeywords = '';
    let autoFocusKeyword = '';
    
    if (item.type === 'blog') {
      autoTitle = `${item.title} | ${item.category} Guide 2025 | Grad Vikalp`;
      autoDescription = `Read "${item.title}" - A comprehensive ${item.category?.toLowerCase()} guide. Get expert insights, tips, and advice for college admissions and academic success.`;
      autoKeywords = `${item.category?.toLowerCase()}, ${item.category?.toLowerCase()} guidance, college admission, education blog, grad vikalp`;
      autoFocusKeyword = `${item.category?.toLowerCase()} guidance`;
    } else if (item.type === 'college') {
      autoTitle = `${item.title} - Complete College Guide, Reviews & Admission Details 2025 | Grad Vikalp`;
      autoDescription = `Complete guide to ${item.title} - Get detailed information about fees, cutoffs, courses, student reviews, facilities, and admission process.`;
      autoKeywords = `${item.title}, college reviews, admission, fees, cutoffs, ${item.category?.toLowerCase()} college`;
      autoFocusKeyword = `${item.title} college`;
    }
    
    setEditingItem(prev => ({
      ...prev!,
      seo: {
        ...prev!.seo,
        title: autoTitle,
        description: autoDescription,
        keywords: autoKeywords,
        focus_keyword: autoFocusKeyword,
        og_title: autoTitle,
        og_description: autoDescription,
        twitter_title: autoTitle,
        twitter_description: autoDescription
      }
    }));
    
    toast.success('SEO fields auto-generated!');
  };

  const copyFromTitle = () => {
    if (!editingItem) return;
    
    setEditingItem(prev => ({
      ...prev!,
      seo: {
        ...prev!.seo,
        og_title: prev!.seo.title || '',
        twitter_title: prev!.seo.title || ''
      }
    }));
    
    toast.success('Title copied to social media fields!');
  };

  const copyFromDescription = () => {
    if (!editingItem) return;
    
    setEditingItem(prev => ({
      ...prev!,
      seo: {
        ...prev!.seo,
        og_description: prev!.seo.description || '',
        twitter_description: prev!.seo.description || ''
      }
    }));
    
    toast.success('Description copied to social media fields!');
  };

  const filteredContentItems = contentItems.filter(item => {
    if (activeTab === 'pages') return item.type === 'page';
    if (activeTab === 'blogs') return item.type === 'blog';
    if (activeTab === 'colleges') return item.type === 'college';
    return true;
  }).filter(item => {
    if (!searchQuery) return true;
    return item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
           item.slug.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5">
          <div className="text-center">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <AlertCircle className="text-warning mx-auto mb-3" size={64} />
                </div>
                <h1 className="h2 text-dark mb-3">Access Restricted</h1>
                <p className="text-muted mb-4">
                  You need admin privileges to access the SEO Management system.
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                  <Link to="/" className="btn btn-primary">
                    Back to Home
                  </Link>
                  <Link to="/contact" className="btn btn-outline-primary">
                    Contact Support
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading SEO management...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
      <SEOHead
        title="SEO Manager - Comprehensive SEO Management | Grad Vikalp"
        description="Manage all SEO settings for pages, blog posts, and college pages in one centralized location."
        keywords="SEO management, meta tags, search optimization, content SEO"
        url="/admin/seo-manager"
        type="website"
      />
      <div className="container py-4">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <div className="bg-primary rounded-3 p-3">
              <Globe className="text-white" size={32} />
            </div>
            <div>
              <h1 className="h2 fw-bold text-dark mb-1">SEO Manager</h1>
              <p className="text-muted mb-0">Comprehensive SEO management for all content</p>
            </div>
          </div>
          <Link to="/admin" className="btn btn-outline-secondary">
            Back to Admin
          </Link>
        </div>

        {/* Tabs */}
        <div className="card border-0 shadow-lg mb-4">
          <div className="card-header bg-white border-bottom">
            <ul className="nav nav-tabs card-header-tabs">
              <li className="nav-item">
                <button 
                  onClick={() => setActiveTab('pages')}
                  className={`nav-link ${activeTab === 'pages' ? 'active' : ''}`}
                >
                  <FileText size={16} className="me-2" />
                  Pages ({pagesList.length})
                </button>
              </li>
              <li className="nav-item">
                <button 
                  onClick={() => setActiveTab('blogs')}
                  className={`nav-link ${activeTab === 'blogs' ? 'active' : ''}`}
                >
                  <FileText size={16} className="me-2" />
                  Blog Posts ({contentItems.filter(i => i.type === 'blog').length})
                </button>
              </li>
              <li className="nav-item">
                <button 
                  onClick={() => setActiveTab('colleges')}
                  className={`nav-link ${activeTab === 'colleges' ? 'active' : ''}`}
                >
                  <Settings size={16} className="me-2" />
                  Colleges ({contentItems.filter(i => i.type === 'college').length})
                </button>
              </li>
            </ul>
          </div>

          <div className="card-body p-4">
            {/* Search */}
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <div className="position-relative">
                  <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={16} />
                  <input
                    type="text"
                    placeholder="Search content..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="form-control ps-5"
                  />
                </div>
              </div>
              <div className="col-md-6 text-end">
                {activeTab === 'pages' && (
                  <button
                    onClick={savePageSeoSettings}
                    disabled={saving}
                    className="btn btn-success d-flex align-items-center gap-2 ms-auto"
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Save All Page Settings
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Content List */}
            <div className="row g-4">
              {filteredContentItems.map((item) => (
                <div key={item.id} className="col-12">
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="card border"
                  >
                    <div className="card-body p-4">
                      <div className="row g-3">
                        <div className="col-md-8">
                          <div className="d-flex align-items-start gap-3">
                            <div className={`p-2 rounded ${
                              item.type === 'page' ? 'bg-primary bg-opacity-10' :
                              item.type === 'blog' ? 'bg-success bg-opacity-10' :
                              'bg-info bg-opacity-10'
                            }`}>
                              {item.type === 'page' && <FileText className="text-primary" size={20} />}
                              {item.type === 'blog' && <FileText className="text-success" size={20} />}
                              {item.type === 'college' && <Settings className="text-info" size={20} />}
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center gap-2 mb-2">
                                <h5 className="fw-bold text-dark mb-0">{item.title}</h5>
                                <span className={`badge ${
                                  item.type === 'page' ? 'bg-primary' :
                                  item.type === 'blog' ? 'bg-success' :
                                  'bg-info'
                                }`}>
                                  {item.type}
                                </span>
                                {item.category && (
                                  <span className="badge bg-secondary">{item.category}</span>
                                )}
                                {item.published === false && (
                                  <span className="badge bg-warning">Draft</span>
                                )}
                              </div>
                              
                              <div className="vstack gap-1 small text-muted">
                                <div className="d-flex align-items-center gap-2">
                                  <Globe size={14} />
                                  <span className="text-primary">{item.url}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <Tag size={14} />
                                  <span>Title: {item.seo.title || 'Not set'}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <FileText size={14} />
                                  <span>Description: {item.seo.description ? `${item.seo.description.substring(0, 60)}...` : 'Not set'}</span>
                                </div>
                                {item.seo.focus_keyword && (
                                  <div className="d-flex align-items-center gap-2">
                                    <Tag size={14} />
                                    <span>Focus: {item.seo.focus_keyword}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-md-4 text-end">
                          <div className="d-flex flex-column gap-2">
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-outline-info btn-sm d-flex align-items-center gap-2"
                            >
                              <Eye size={14} />
                              View Page
                            </a>
                            {item.type !== 'page' ? (
                              <button
                                onClick={() => editContentSeo(item)}
                                className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
                              >
                                <Edit3 size={14} />
                                Edit SEO
                              </button>
                            ) : (
                              <span className="text-muted small">Edit in Pages tab</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>

            {/* Pages Tab Content */}
            {activeTab === 'pages' && (
              <div className="mt-4">
                <div className="row g-4">
                  {pagesList.map((page) => (
                    <div key={page.key} className="col-12">
                      <div className="card border">
                        <div className="card-header bg-light">
                          <h6 className="mb-0 fw-bold">{page.name} SEO Settings</h6>
                        </div>
                        <div className="card-body">
                          <div className="row g-3">
                            <div className="col-md-6">
                              <label className="form-label fw-semibold">Page Title *</label>
                              <input
                                type="text"
                                value={pageSeoSettings[page.key]?.title || ''}
                                onChange={(e) => handlePageSeoChange(page.key, 'title', e.target.value)}
                                className="form-control"
                                placeholder="Enter page title"
                              />
                              <div className="form-text">
                                Length: {pageSeoSettings[page.key]?.title?.length || 0}/60 characters
                              </div>
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-semibold">Focus Keyword</label>
                              <input
                                type="text"
                                value={pageSeoSettings[page.key]?.focus_keyword || ''}
                                onChange={(e) => handlePageSeoChange(page.key, 'focus_keyword', e.target.value)}
                                className="form-control"
                                placeholder="Primary keyword to rank for"
                              />
                            </div>
                            <div className="col-12">
                              <label className="form-label fw-semibold">Meta Description *</label>
                              <textarea
                                value={pageSeoSettings[page.key]?.description || ''}
                                onChange={(e) => handlePageSeoChange(page.key, 'description', e.target.value)}
                                className="form-control"
                                rows={3}
                                placeholder="Enter meta description"
                              />
                              <div className="form-text">
                                Length: {pageSeoSettings[page.key]?.description?.length || 0}/160 characters
                              </div>
                            </div>
                            <div className="col-12">
                              <label className="form-label fw-semibold">Meta Keywords</label>
                              <textarea
                                value={pageSeoSettings[page.key]?.keywords || ''}
                                onChange={(e) => handlePageSeoChange(page.key, 'keywords', e.target.value)}
                                className="form-control"
                                rows={2}
                                placeholder="Enter keywords separated by commas"
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-semibold">Social Image URL</label>
                              <input
                                type="text"
                                value={pageSeoSettings[page.key]?.image || ''}
                                onChange={(e) => handlePageSeoChange(page.key, 'image', e.target.value)}
                                className="form-control"
                                placeholder="Image URL for social sharing"
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label fw-semibold">Schema Type</label>
                              <select
                                value={pageSeoSettings[page.key]?.schema_type || 'WebPage'}
                                onChange={(e) => handlePageSeoChange(page.key, 'schema_type', e.target.value)}
                                className="form-select"
                              >
                                <option value="WebPage">WebPage</option>
                                <option value="WebSite">WebSite</option>
                                <option value="AboutPage">AboutPage</option>
                                <option value="ContactPage">ContactPage</option>
                                <option value="CollectionPage">CollectionPage</option>
                                <option value="WebApplication">WebApplication</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {filteredContentItems.length === 0 && (
              <div className="text-center py-5">
                <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
                  <div className="card-body p-5">
                    <div className="mb-4">
                      <Globe className="text-muted mx-auto mb-3" size={64} />
                    </div>
                    <h3 className="h4 text-dark mb-3">No content found</h3>
                    <p className="text-muted mb-4">
                      {searchQuery 
                        ? 'No content matches your search criteria.'
                        : `No ${activeTab} found.`
                      }
                    </p>
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="btn btn-primary"
                      >
                        Clear Search
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Edit Content SEO Modal */}
        {showEditForm && editingItem && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    Edit SEO - {editingItem.title}
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => {
                      setShowEditForm(false);
                      setEditingItem(null);
                    }}
                  ></button>
                </div>
                <div className="modal-body p-4">
                  {/* Quick Actions */}
                  <div className="d-flex gap-2 mb-4">
                    <button
                      onClick={() => generateSeoFromContent(editingItem)}
                      className="btn btn-success btn-sm d-flex align-items-center gap-2"
                    >
                      <RefreshCw size={14} />
                      Auto-Generate SEO
                    </button>
                    <button
                      onClick={copyFromTitle}
                      className="btn btn-info btn-sm d-flex align-items-center gap-2"
                    >
                      <Copy size={14} />
                      Copy Title to Social
                    </button>
                    <button
                      onClick={copyFromDescription}
                      className="btn btn-warning btn-sm d-flex align-items-center gap-2"
                    >
                      <Copy size={14} />
                      Copy Description to Social
                    </button>
                  </div>

                  {/* Basic SEO */}
                  <div className="mb-4">
                    <h6 className="fw-bold text-dark mb-3">🔍 Basic SEO</h6>
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label fw-semibold">Meta Title *</label>
                        <input
                          type="text"
                          value={editingItem.seo.title || ''}
                          onChange={(e) => handleContentSeoChange('title', e.target.value)}
                          className="form-control"
                          placeholder="SEO-optimized title"
                        />
                        <div className="form-text">
                          Recommended: 50-60 characters. Current: {editingItem.seo.title?.length || 0}
                        </div>
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold">Meta Description *</label>
                        <textarea
                          value={editingItem.seo.description || ''}
                          onChange={(e) => handleContentSeoChange('description', e.target.value)}
                          className="form-control"
                          rows={3}
                          placeholder="Description for search results"
                        />
                        <div className="form-text">
                          Recommended: 150-160 characters. Current: {editingItem.seo.description?.length || 0}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Meta Keywords</label>
                        <textarea
                          value={editingItem.seo.keywords || ''}
                          onChange={(e) => handleContentSeoChange('keywords', e.target.value)}
                          className="form-control"
                          rows={2}
                          placeholder="Keywords separated by commas"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Focus Keyword</label>
                        <input
                          type="text"
                          value={editingItem.seo.focus_keyword || ''}
                          onChange={(e) => handleContentSeoChange('focus_keyword', e.target.value)}
                          className="form-control"
                          placeholder="Primary keyword to rank for"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Canonical URL</label>
                        <input
                          type="text"
                          value={editingItem.seo.canonical_url || ''}
                          onChange={(e) => handleContentSeoChange('canonical_url', e.target.value)}
                          className="form-control"
                          placeholder="/page-url"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Schema Type</label>
                        <select
                          value={editingItem.seo.schema_type || ''}
                          onChange={(e) => handleContentSeoChange('schema_type', e.target.value)}
                          className="form-select"
                        >
                          <option value="">Select Schema Type</option>
                          <option value="WebPage">WebPage</option>
                          <option value="BlogPosting">BlogPosting</option>
                          <option value="Article">Article</option>
                          <option value="EducationalOrganization">EducationalOrganization</option>
                          <option value="Organization">Organization</option>
                          <option value="WebApplication">WebApplication</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Social Media SEO */}
                  <div className="mb-4">
                    <h6 className="fw-bold text-dark mb-3">📱 Social Media SEO</h6>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Open Graph Title</label>
                        <input
                          type="text"
                          value={editingItem.seo.og_title || ''}
                          onChange={(e) => handleContentSeoChange('og_title', e.target.value)}
                          className="form-control"
                          placeholder="Title for Facebook/LinkedIn"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Twitter Title</label>
                        <input
                          type="text"
                          value={editingItem.seo.twitter_title || ''}
                          onChange={(e) => handleContentSeoChange('twitter_title', e.target.value)}
                          className="form-control"
                          placeholder="Title for Twitter"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Open Graph Description</label>
                        <textarea
                          value={editingItem.seo.og_description || ''}
                          onChange={(e) => handleContentSeoChange('og_description', e.target.value)}
                          className="form-control"
                          rows={2}
                          placeholder="Description for Facebook/LinkedIn"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Twitter Description</label>
                        <textarea
                          value={editingItem.seo.twitter_description || ''}
                          onChange={(e) => handleContentSeoChange('twitter_description', e.target.value)}
                          className="form-control"
                          rows={2}
                          placeholder="Description for Twitter"
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold">Social Image URL</label>
                        <input
                          type="url"
                          value={editingItem.seo.og_image || editingItem.seo.image || ''}
                          onChange={(e) => {
                            handleContentSeoChange('og_image', e.target.value);
                            handleContentSeoChange('image', e.target.value);
                          }}
                          className="form-control"
                          placeholder="https://images.pexels.com/..."
                        />
                        <div className="form-text">
                          Recommended: 1200x630px for optimal social sharing
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SEO Preview */}
                  <div className="mb-4">
                    <h6 className="fw-bold text-dark mb-3">👁️ SEO Preview</h6>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <h6 className="small fw-semibold text-muted mb-2">Google Search Result:</h6>
                        <div className="border p-3 rounded bg-white">
                          <div style={{ fontFamily: 'Arial, sans-serif' }}>
                            <div style={{ color: '#1a0dab', fontSize: '18px', marginBottom: '4px' }}>
                              {editingItem.seo.title || editingItem.title}
                            </div>
                            <div style={{ color: '#006621', fontSize: '14px', marginBottom: '4px' }}>
                              https://gradvikalp.com{editingItem.seo.canonical_url || editingItem.url}
                            </div>
                            <div style={{ color: '#545454', fontSize: '14px', lineHeight: '1.4' }}>
                              {editingItem.seo.description || 'Meta description will appear here...'}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <h6 className="small fw-semibold text-muted mb-2">Social Media Preview:</h6>
                        <div className="border p-3 rounded bg-white">
                          <div style={{ maxWidth: '400px' }}>
                            <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                              <div style={{ height: '200px', background: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {editingItem.seo.og_image || editingItem.seo.image ? (
                                  <img 
                                    src={editingItem.seo.og_image || editingItem.seo.image} 
                                    alt="Social preview" 
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} 
                                  />
                                ) : (
                                  <div style={{ color: '#aaa', textAlign: 'center' }}>
                                    <ImageIcon size={48} />
                                    <div>Image Preview</div>
                                  </div>
                                )}
                              </div>
                              <div style={{ padding: '12px' }}>
                                <div style={{ color: '#385898', fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
                                  {editingItem.seo.og_title || editingItem.seo.title || editingItem.title}
                                </div>
                                <div style={{ color: '#606770', fontSize: '14px', marginBottom: '4px' }}>
                                  gradvikalp.com
                                </div>
                                <div style={{ color: '#606770', fontSize: '14px', lineHeight: '1.4' }}>
                                  {editingItem.seo.og_description || editingItem.seo.description || 'Description will appear here...'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button
                    onClick={() => {
                      setShowEditForm(false);
                      setEditingItem(null);
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveContentSeo}
                    disabled={saving}
                    className="btn btn-primary d-flex align-items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Save SEO Settings
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SEOManager;